import { useEffect, useRef, useState } from 'react';

import { cn, mString } from 'lib/helpers/utility';

import Input from 'components/shared/Input/Input';
import Button from 'components/shared/Button/Button';
import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';

const positionAndWrapDropdown = (triggerEl, dropdownEl) => {
  const triggerRect = triggerEl.getBoundingClientRect();
  const dropdownRect = dropdownEl.getBoundingClientRect();

  const willOverflowRight = triggerRect.left + dropdownRect.width > window.innerWidth - 8;

  if (willOverflowRight) {
    dropdownEl.classList.add('flex-wrap');
    dropdownEl.classList.add('w-[172px]');
  } else {
    dropdownEl.classList.remove('flex-wrap');
    dropdownEl.classList.remove('w-[172px]');
  }
};

const toNumber = (value) => (value === '' ? 0 : Number(value));

const getComponents = (formula, inputValues, onChange, numeric) => {
  if (formula === 'is equal to' || formula === 'is less than' || formula === 'is more than') {
    if (numeric) {
      return (
        <Input
          type="number"
          min={0}
          placeholder="Enter number"
          className="w-[172px] min-w-[172px]"
          value={inputValues ?? ''}
          onChange={(e) => onChange(toNumber(e.target.value))}
        />
      );
    }
    return (
      <CurrencyInput
        placeholder="Enter amount"
        className="w-[172px] min-w-[172px]"
        value={inputValues}
        onValueChange={onChange}
      />
    );
  }

  if (formula === 'amount range is' || formula === 'amount range is not') {
    if (numeric) {
      return (
        <>
          <Input
            type="number"
            min={0}
            placeholder="Minimum"
            className="w-[172px] min-w-[172px]"
            value={inputValues?.from ?? ''}
            onChange={(e) => onChange(toNumber(e.target.value), 'from')}
          />
          <Input
            type="number"
            min={0}
            placeholder="Maximum"
            className="w-[172px] min-w-[172px]"
            value={inputValues?.to ?? ''}
            onChange={(e) => onChange(toNumber(e.target.value), 'to')}
          />
        </>
      );
    }
    return (
      <>
        <CurrencyInput
          placeholder="Minimum amount"
          className="w-[172px] min-w-[172px]"
          value={inputValues.from}
          onValueChange={(value) => onChange(value, 'from')}
        />
        <CurrencyInput
          placeholder="Maximum amount"
          className="w-[172px] min-w-[172px]"
          value={inputValues.to}
          onValueChange={(value) => onChange(value, 'to')}
        />
      </>
    );
  }
};

const getInputValues = (formula, values) => {
  if (formula === 'amount range is' || formula === 'amount range is not') {
    return { from: values?.from || 0, to: values?.to || 0 };
  }

  return values || 0;
};

const getButtonText = (values, formula, numeric) => {
  const newValues = getInputValues(formula, values);
  const format = (value) => (numeric ? String(value) : mString(value));

  if (
    (newValues?.from || typeof newValues?.from === 'number') &&
    (newValues?.to || typeof newValues?.to === 'number') &&
    (formula === 'amount range is' || formula === 'amount range is not')
  ) {
    return `${format(newValues?.from)} - ${format(newValues?.to)}`;
  }

  if (
    typeof newValues === 'number' &&
    (formula === 'is equal to' || formula === 'is less than' || formula === 'is more than')
  ) {
    return format(newValues);
  }

  return numeric ? 'Enter number' : 'Enter amount';
};

const RangeSelector = ({
  initialOpenState = false,
  values,
  onApply,
  formula,
  position,
  numeric = false,
  ...rest
}) => {
  const [open, setOpen] = useState(initialOpenState);
  const [inputValues, setInputValues] = useState(values);

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const buttonText = getButtonText(values, formula, numeric);

  const onChangeHandler = (value, key) => {
    if (key) {
      setInputValues((prev) => ({ ...prev, [key]: value }));
      return;
    }

    setInputValues(value);
  };

  const onApplyHandler = () => {
    const newValues = getInputValues(formula, inputValues);
    onApply(newValues);
    setOpen(false);
  };

  useEffect(() => {
    const updatePosition = () => {
      if (open && triggerRef.current && dropdownRef.current) {
        positionAndWrapDropdown(triggerRef.current, dropdownRef.current);
      }
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  return (
    <div className="relative">
      <Button
        ref={triggerRef}
        onClick={() => setOpen((prev) => !prev)}
        className="whitespace-nowrap"
        text={buttonText}
        {...rest}
      />
      {open && (
        <div
          className={cn(
            'absolute z-10 flex items-center gap-2 rounded bg-white p-2 shadow-xs',
            position === 'right' ? 'left-0' : 'right-0'
          )}>
          <div className="flex w-full justify-end gap-2" ref={dropdownRef}>
            {getComponents(formula, inputValues, onChangeHandler, numeric)}
            <Button text="Apply" onClick={onApplyHandler} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeSelector;
